import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { Theme } from "baseui/theme";
import { PlatformTheme } from "kanopi-components/theme/useTheme";
import { useThemeOverrides } from "./util/useThemeOverrides";
import { TBrandLogo } from "./components/BrandLogo/BrandLogo";
import { useUser } from "./auth/useUser";

type TThemeContext = {
  basewebDefaultTheme: Theme;
  platformDefaultTheme: Theme;
  theme: Theme;
  logo: TBrandLogo;
  getThemeWithOverrides: (overrides?: PlatformTheme) => Theme;
};
const ThemeContext = createContext<{
  context: TThemeContext;
  setContext: Dispatch<SetStateAction<TThemeContext>>;
}>(undefined);

type TThemeOverrides = { logo: TBrandLogo; themeOverride: PlatformTheme };

type TUseThemeContext = {
  context: TThemeContext;
  themeLoading: boolean;
  refreshTheme: () => void;
  resetToPlatformDefaultTheme: () => void;
  setThemeLoading: (loading: boolean) => void;
  setTheme: (theme: Theme) => void;
  setContext: Dispatch<SetStateAction<TThemeContext>>;
};
const useThemeContext = (): TUseThemeContext => {
  const { context, setContext } = useContext(ThemeContext);
  const user = useUser();
  const [themeLoading, setThemeLoading] = useState(true);
  const { loadThemeOverrides, loading: themeOverridesLoading } = useThemeOverrides();

  const getSessionThemeKey = () => {
    return `${user?.latestReferrer?.key || ""}_${user?.organization?.key || ""}_theme`;
  };

  const getSessionTheme = (): TThemeOverrides => {
    const themeKey = getSessionThemeKey();
    const sessionTheme = sessionStorage.getItem(themeKey);
    return sessionTheme ? JSON.parse(sessionTheme) : null;
  };

  const setThemeInSession = (theme: TThemeOverrides) => {
    const themeKey = getSessionThemeKey();
    sessionStorage.setItem(themeKey, JSON.stringify(theme));
  };

  const setTheme = (theme: Theme) => {
    const _context = { ...context };
    _context.theme = theme;
    setTimeout(() => {
      setContext(_context);
    });
  };

  /**
   * Fetch theme overrides from backend and generates a theme.
   * Generates a new theme only when the theme for distributionPartner or organisation is not
   * already created within the user session.
   * @returns void
   */
  const refreshTheme = () => {
    if (!getSessionTheme()) {
      loadThemeOverrides(
        (overrides: { logo: TBrandLogo; themeOverride: PlatformTheme }) => {
          const _context = { ...context };
          _context.theme = context.getThemeWithOverrides(overrides?.themeOverride);
          _context.logo = overrides?.logo;
          setThemeInSession({
            logo: overrides?.logo,
            themeOverride: overrides?.themeOverride,
          });
          setTimeout(() => {
            setContext(_context);
          });
        }
      );
    } else {
      const _override = getSessionTheme();
      const _context = { ...context };
      _context.theme = context.getThemeWithOverrides(_override?.themeOverride);
      _context.logo = _override?.logo;
      setTimeout(() => {
        setContext(_context);
      });
    }
  };

  const resetToPlatformDefaultTheme = () => {
    const _context = { ...context };
    _context.theme = _context.platformDefaultTheme;
    setTimeout(() => {
      setContext(_context);
    });
  };

  useEffect(() => {
    setThemeLoading(themeOverridesLoading);
  }, [themeOverridesLoading]);

  if (!context) {
    throw new Error("useThemeContext must be used inside ThemeContextProvider");
  }
  return {
    context,
    themeLoading,
    refreshTheme,
    resetToPlatformDefaultTheme,
    setThemeLoading,
    setTheme,
    setContext,
  };
};

export { ThemeContext, useThemeContext };
